import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroBackdrop from "@/components/HeroBackdrop";
import HeroActions from "@/components/HeroActions";
import { VEILLE_COMMERCIALE } from "@/config/siteFlags";

/**
 * Shared layout for internal pages. Wraps content with the Navbar, an
 * optional page header (eyebrow + H1 + intro), a footer CTA pointing to
 * the "Audit initial" funnel, and the global Footer.
 */
export default function PageLayout({
  eyebrow,
  title,
  intro,
  introNote,
  children,
  hideFooterCta = false,
  ctaTitle = "Des informations sur vous apparaissent en ligne ?",
  ctaButton = "Demander un audit initial",
  ctaTo = "/audit-initial",
  pattern,
}) {
  return (
    <div className="bg-[#FAF9F7] text-[#181A1D] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {title ? (
          <section
            data-testid="page-header"
            className="relative overflow-hidden bg-[#FAF9F7] grain"
          >
            {pattern ? <HeroBackdrop variant={pattern} /> : null}
            <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-14 lg:pb-16">
              {eyebrow ? (
                <p className="label-eyebrow mb-7">{eyebrow}</p>
              ) : null}
              <h1
                data-testid="page-title"
                className="font-sans font-semibold text-[#181A1D] text-balance tracking-tight leading-[1.06] text-4xl md:text-5xl lg:text-[56px] max-w-4xl animate-fade-up"
              >
                {title}
              </h1>
              <div aria-hidden="true" data-testid="h1-marker" className="h1-marker mt-7" />
              {intro ? (
                <p
                  data-testid="page-intro"
                  className="mt-8 max-w-2xl text-base lg:text-lg leading-relaxed text-[#52565C] text-pretty animate-fade-up"
                  style={{ animationDelay: "120ms" }}
                >
                  {intro}
                </p>
              ) : null}
              {introNote ? (
                <p
                  data-testid="page-intro-note"
                  className="mt-5 max-w-2xl text-[13px] leading-relaxed text-[#8B8F95] animate-fade-up"
                  style={{ animationDelay: "180ms" }}
                >
                  {introNote}
                </p>
              ) : null}
              <HeroActions />
            </div>
            <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
              <div className="h-px bg-[#E2DEDA]" />
            </div>
          </section>
        ) : null}

        {children}

        {!hideFooterCta && !VEILLE_COMMERCIALE ? (
          <section
            data-testid="page-footer-cta"
            className="relative bg-[#181A1D]"
          >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#E94B3C] mb-5">
                  Étape suivante
                </p>
                <h2 className="font-sans font-semibold text-[#FAF9F7] text-3xl lg:text-4xl leading-tight tracking-tight text-balance">
                  {ctaTitle}
                </h2>
              </div>
              <div className="lg:col-span-5 lg:flex lg:justify-end">
                <Link
                  to={ctaTo}
                  data-testid="page-footer-cta-link"
                  className="btn-primary"
                >
                  {ctaButton}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
